import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TypedMutationConfig } from '@/lib/react-query';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { TCareConfigurationNotification } from '../types/notification.type';

export const UpdateCareConfigurationNotificationValidationSchema = z.object({
  LABEL_CUSTOMER_ADDED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  LABEL_CHAT_ADDED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  LABEL_CASE_ADDED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  SLA_UPDATED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  CSAT_ENABLED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  CSAT_UPDATED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  TEAM_MEMBER_DELETED: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  CSAT_DISABLED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  LABEL_CUSTOMER_DELETED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  LABEL_CHAT_DELETED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  LABEL_CASE_DELETED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  LABEL_CUSTOMER_UPDATED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  LABEL_CHAT_UPDATED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  LABEL_CASE_UPDATED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  TEAM_MEMBER_ASSIGNMENT: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  NEW_CONVERSATION: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  CHANNELS_AUTO_LABEL_UPDATED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  AUTO_LABEL_UPDATED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  NEW_TEAM_SUPERVISOR_ASSIGNMENT: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  RESOLVED_CASE: z
    .object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
    })
    .optional(),
  CHANNEL_ADDED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  CHANNEL_DELETED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  CLOSE_CONVERSATION: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  CHANNEL_DISCONNECT: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
  TEAM_DELETED: z
    .object({
      app: z.boolean().optional(),
    })
    .optional(),
});
export type TUpdateCareConfigurationNotificationValidation = z.infer<
  typeof UpdateCareConfigurationNotificationValidationSchema
>;
export type TUpdateCareConfigurationNotificationPayload = TUpdateCareConfigurationNotificationValidation;
export type TUpdateCareConfigurationNotificationResponse = ApiResponse<TCareConfigurationNotification>;

/**
 * Cleans the payload to ensure only fields with actual values are sent
 * Removes undefined values and empty objects
 */

export const updateCareConfigurationNotification = async (
  payload: TUpdateCareConfigurationNotificationPayload
): Promise<TUpdateCareConfigurationNotificationResponse> => {
  const url = `/notifications/care-configuration`;

  const payloadData = {
    configurations: payload,
  };

  return axiosInstance.put(url, payloadData);
};

type TUseUpdateCareConfigurationNotificationMutationOptions = {
  mutationConfigOptions?: TypedMutationConfig<typeof updateCareConfigurationNotification, AxiosErrorCustom>;
};

export const useUpdateCareConfigurationNotificationMutation = ({
  mutationConfigOptions,
}: TUseUpdateCareConfigurationNotificationMutationOptions) => {
  const { ...restConfigOptions } = mutationConfigOptions ?? {};

  return useMutation({
    mutationFn: updateCareConfigurationNotification,
    ...restConfigOptions,
  });
};
